import { z } from 'zod';

import { EMPLOYEE_STATUSES, EMPLOYEE_TYPES } from '../employee.js';
import type { EmployeeStatus } from '../employee.js';

/**
 * The employee as the SPA sees it - EMP-005.
 *
 * `../employee.ts` holds the same shape as a hand-written interface, and that
 * duplication is the rule this package works by rather than an oversight: a
 * wire shape is checked on arrival and a domain shape is not, so one is zod and
 * one is not (`error.ts`). The two are asserted to agree in `employee.spec.ts`,
 * which is what stops them drifting.
 *
 * The type and status vocabularies are imported rather than restated. Two
 * definitions of a vocabulary drift the first time only one is edited, and the
 * drift is silent - the same argument `http/workspace.ts` makes about roles.
 */

/**
 * The database puts no ceiling on these columns. The API does: an unbounded
 * string from a user-supplied field is an input-validation gap even when the
 * column would hold it. Deliberately stricter than the schema, never looser.
 */
export const EMPLOYEE_NAME_MAX = 120;
export const EMPLOYEE_ROLE_TITLE_MAX = 120;
export const EMPLOYEE_JOB_DESCRIPTION_MAX = 4000;

/**
 * Which statuses may be addressed in Chat - EMP-009.
 *
 * The rule itself is `RUNNABLE_STATUSES` in `packages/domain`, and the API
 * enforces it: `POST /v1/conversations` answers 409 for an employee outside
 * this set. It is restated here because `packages/domain` is `runtime:node` and
 * ADR-0022 forbids `apps/web` from importing it, so the selector would
 * otherwise have to hardcode the vocabulary and drift the first time the
 * lifecycle gains a state.
 *
 * The same shape `AUTHORIZING_STATUS` takes in `http/workspace.ts`, and the
 * same reason. `packages/domain/src/domain.spec.ts` asserts the two lists hold
 * the same members, so a divergence is a failing build rather than a screen
 * that quietly offers employees the API will refuse.
 */
export const ADDRESSABLE_STATUSES = [
  'PROBATION',
  'ACTIVE',
] as const satisfies readonly EmployeeStatus[];

export const employeeSchema = z
  .object({
    id: z.uuid(),
    workspaceId: z.uuid(),
    name: z.string(),
    employeeType: z.enum(EMPLOYEE_TYPES),
    status: z.enum(EMPLOYEE_STATUSES),
    roleTitle: z.string(),
    jobDescription: z.string(),
    /** Absent until EMP-011 - `employee.policy_id` is nullable and has no table to point at. */
    policyId: z.uuid().optional(),
    createdAt: z.string(),
  })
  .readonly();

export const employeeListResponseSchema = z
  .object({ employees: z.array(employeeSchema).readonly() })
  .readonly();

export const employeeResponseSchema = z.object({ employee: employeeSchema }).readonly();

/**
 * Creating an employee, and the two things a caller cannot choose.
 *
 * **`employeeType` is absent because it is always SPECIALIST.** The Generalist
 * is created by onboarding, one per workspace, and
 * `employee_one_generalist_per_workspace` refuses a second - so offering the
 * choice would be offering a request the database will reject.
 *
 * **`status` is absent because a new employee is always DRAFT.** Where an
 * employee is in the lifecycle is EMP-003's answer, reached by transition and
 * not by assertion at creation.
 *
 * `jobDescription` is optional because EMP-007 owns the editor and an employee
 * can be named before their duties are written. The column is `not null` with
 * no non-empty check, so an empty one is a legal row.
 */
export const createEmployeeRequestSchema = z
  .object({
    name: z.string().trim().min(1).max(EMPLOYEE_NAME_MAX),
    roleTitle: z.string().trim().min(1).max(EMPLOYEE_ROLE_TITLE_MAX),
    jobDescription: z.string().trim().max(EMPLOYEE_JOB_DESCRIPTION_MAX).optional(),
  })
  .readonly();

/**
 * The profile, and only the profile. Status changes have their own route,
 * because a lifecycle transition has rules a field edit does not and mixing the
 * two would let a rejected transition fail a name change.
 *
 * Every field is optional: a PATCH that names no field returns the employee
 * unchanged, which is a correct and idempotent answer rather than an error.
 */
export const updateEmployeeRequestSchema = z
  .object({
    name: z.string().trim().min(1).max(EMPLOYEE_NAME_MAX).optional(),
    roleTitle: z.string().trim().min(1).max(EMPLOYEE_ROLE_TITLE_MAX).optional(),
    jobDescription: z.string().trim().max(EMPLOYEE_JOB_DESCRIPTION_MAX).optional(),
  })
  .readonly();

export const changeEmployeeStatusRequestSchema = z
  .object({ status: z.enum(EMPLOYEE_STATUSES) })
  .readonly();

export type EmployeeResponse = z.infer<typeof employeeResponseSchema>;
export type EmployeeListResponse = z.infer<typeof employeeListResponseSchema>;
export type CreateEmployeeRequest = z.infer<typeof createEmployeeRequestSchema>;
export type UpdateEmployeeRequest = z.infer<typeof updateEmployeeRequestSchema>;
export type ChangeEmployeeStatusRequest = z.infer<typeof changeEmployeeStatusRequestSchema>;
